import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { navigate, useToast, useTRPC } from '@barev/web';
import { useMemo, useState } from 'react';
import { errText, useT, type BoLocale } from '../i18n.tsx';
import { Avatar, Dialog, Field, I, PlanBadge, Skeleton } from '../components/kit.tsx';
import { MapView, parseCoords } from '../components/map.tsx';
import { CopyLink } from './Agencies.tsx';
import { VENUE_CATEGORIES } from './Crm.tsx';

type L5 = { ru: string; en: string; zh: string; hy: string; es: string };
const EMPTY: L5 = { ru: '', en: '', zh: '', hy: '', es: '' };
const pickLang = (v: Record<string, string> | null | undefined, l: BoLocale) => (v ? v[l] || v.ru || v.en || '' : '');

export function Venues() {
  const { t, date } = useT();
  const trpc = useTRPC();
  const list = useQuery(trpc.bo.partners.venues.queryOptions());
  const [creating, setCreating] = useState(false);
  return (
    <>
      <div className="bo-head">
        <h1 className="bo-title">{t('vn.title')}</h1>
        <button type="button" className="bo-btn bo-btn--accent" onClick={() => setCreating(true)}>
          <I name="plus" size={16} />
          {t('vn.add')}
        </button>
      </div>
      {!list.data ? (
        <Skeleton h={240} />
      ) : list.data.length === 0 ? (
        <div className="bo-panel bo-empty">{t('vn.empty')}</div>
      ) : (
        <div className="bo-panel" style={{ padding: 8 }}>
          <table className="bo-table bo-ptable">
            <thead>
              <tr>
                <th>{t('vn.col.name')}</th>
                <th className="bo-r">{t('vn.col.offers')}</th>
                <th className="bo-r">{t('vn.col.staff')}</th>
                <th className="bo-r">{t('vn.col.visits')}</th>
                <th>{t('vn.col.last')}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((v) => (
                <tr key={v.id} onClick={() => navigate(`/venues/${v.id}`)} data-off={!v.active || undefined}>
                  <td>
                    <a href={`#/venues/${v.id}`} className="bo-who" onClick={(e) => e.stopPropagation()}>
                      <Avatar name={v.name} id={v.id} size={38} />
                      <span style={{ minWidth: 0 }}>
                        <span className="bo-who__name">{v.name}</span>
                        <span className="bo-who__meta">{[t(`venue.cat.${v.category}`), v.address, v.sales?.name].filter(Boolean).join(' · ')}</span>
                        <span className="bo-tags" style={{ marginTop: 4 }}>
                          {!v.active && <span className="bo-chip bo-chip--danger">{t('ag.inactive')}</span>}
                          {v.members === 0 && <span className="bo-chip bo-chip--warn">{t('vn.noOwner')}</span>}
                          {v.offers === 0 && <span className="bo-chip">{t('vn.noOffers')}</span>}
                        </span>
                      </span>
                    </a>
                  </td>
                  <td className="bo-r bo-num">{v.offers}</td>
                  <td className="bo-r bo-num">{v.members}</td>
                  <td className="bo-r bo-num">{v.visits30}</td>
                  <td className="bo-small">{v.lastVisit ? date(v.lastVisit) : <span className="bo-faint">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="bo-hint" style={{ padding: '10px 12px 4px' }}>
            {t('vn.hint')}
          </p>
        </div>
      )}
      {creating && <VenueDialog onClose={() => setCreating(false)} />}
    </>
  );
}

export function VenueCard({ id }: { id: string }) {
  const { t, locale, date, dateTime } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const v = useQuery(trpc.bo.partners.venue.queryOptions({ id }));
  const [dlg, setDlg] = useState<'edit' | { offer?: NonNullable<typeof v.data>['offers'][number] } | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: trpc.bo.partners.venue.queryKey({ id }) });
    qc.invalidateQueries({ queryKey: trpc.bo.partners.venues.queryKey() });
  };
  const onError = (e: unknown) => toast(errText(t, e), 'error');
  const invite = useMutation(trpc.bo.partners.ownerInvite.mutationOptions({ onSuccess: refresh, onError }));
  const removeMember = useMutation(trpc.bo.partners.removeMember.mutationOptions({ onSuccess: refresh, onError }));
  const points = useMemo(() => (v.data?.lat != null && v.data.lng != null ? [{ id: v.data.id, lat: v.data.lat, lng: v.data.lng, title: v.data.name, color: '#b8235a', strong: true }] : []), [v.data]);

  const back = (
    <a className="bo-back" href="#/venues">
      <I name="back" size={16} />
      {t('vn.back')}
    </a>
  );
  if (!v.data)
    return (
      <>
        {back}
        {v.isError ? <div className="bo-panel bo-empty">{errText(t, v.error)}</div> : <Skeleton h={300} />}
      </>
    );
  const d = v.data;
  const maxWeek = Math.max(1, ...d.weeks.map((w) => w.checks + w.redeemed));
  const hasOwner = d.members.some((m) => m.role === 'OWNER');

  return (
    <>
      {back}
      <header className="bo-profile">
        <Avatar name={d.name} id={d.id} size={64} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <h1 className="bo-title" style={{ fontSize: 'clamp(26px, 3.4vw, 38px)' }}>
            {d.name}
          </h1>
          <div className="bo-muted" style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
            <span>{t(`venue.cat.${d.category}`)}</span>
            {d.address && <span>{d.address}</span>}
            {d.phone && <a href={`tel:${d.phone.replace(/[^\d+]/g, '')}`}>{d.phone}</a>}
            {d.sales && <span>{t('ag.byS', { name: d.sales.name })}</span>}
            {!d.active && <span className="bo-chip bo-chip--danger">{t('ag.inactive')}</span>}
          </div>
          {pickLang(d.offer, locale) && <p style={{ margin: '10px 0 0' }}>{pickLang(d.offer, locale)}</p>}
        </div>
        <button type="button" className="bo-btn" onClick={() => setDlg('edit')}>
          {t('prv.edit')}
        </button>
      </header>

      {!hasOwner && (
        <div className="bo-banner">
          <span>{t('vn.ownerHint')}</span>
          {d.ownerInviteUrl ? (
            <CopyLink url={d.ownerInviteUrl} />
          ) : (
            <button type="button" className="bo-btn bo-btn--sm" onClick={() => invite.mutate({ id })} disabled={invite.isPending}>
              {t('vn.makeLink')}
            </button>
          )}
        </div>
      )}

      <div className="bo-client-grid">
        <div className="bo-col">
          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('vn.offers')}</h2>
              <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" onClick={() => setDlg({})}>
                <I name="plus" size={16} />
                {t('vn.addOffer')}
              </button>
            </div>
            {d.offers.length === 0 ? (
              <p className="bo-hint">{t('vn.noOffersHint')}</p>
            ) : (
              <ul className="bo-list">
                {d.offers.map((o) => (
                  <li key={o.id} data-off={!o.active || undefined} style={{ opacity: o.active ? 1 : 0.5 }}>
                    <PlanBadge plan={o.minLevel} t={t} />
                    <span className="bo-grow" style={{ whiteSpace: 'normal' }}>
                      <b>{pickLang(o.title, locale)}</b>
                      <span className="bo-small bo-muted" style={{ display: 'block' }}>
                        {o.minLevel ? t(`vn.level.${o.minLevel}`) : t('vn.level.any')} · {t(`vn.limit.${o.limit}`, { n: o.limitCount })}
                        {!o.active && ` · ${t('vn.offerOff')}`}
                      </span>
                    </span>
                    <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" onClick={() => setDlg({ offer: o })}>
                      {t('prv.edit')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('vn.activity')}</h2>
              <span className="bo-faint bo-small">{t('vn.weeks8')}</span>
            </div>
            <div className="bo-weeks">
              {d.weeks.map((w) => (
                <div key={w.from.toISOString()} title={`${date(w.from)}: ${w.checks} / ${w.redeemed}`}>
                  <span className="bo-weeks__bar" style={{ height: `${((w.checks + w.redeemed) / maxWeek) * 100}%` }}>
                    <span style={{ height: `${w.checks + w.redeemed ? (w.redeemed / (w.checks + w.redeemed)) * 100 : 0}%` }} />
                  </span>
                  <span className="bo-small bo-faint">{w.checks + w.redeemed}</span>
                </div>
              ))}
            </div>
            <div className="bo-chart__legend" style={{ marginTop: 8, color: 'var(--bc-ink-2)', justifyContent: 'flex-start', gap: 18 }}>
              <span className="bo-key" style={{ ['--key' as string]: 'var(--bc-blue)' }}>{t('vn.checks')}</span>
              <span className="bo-key" style={{ ['--key' as string]: 'var(--bc-accent)' }}>{t('vn.redeemed')}</span>
            </div>
            {d.recent.length > 0 && (
              <ul className="bo-list" style={{ marginTop: 12 }}>
                {d.recent.slice(0, 12).map((u) => (
                  <li key={u.id}>
                    <span className="bo-feed__time">{dateTime(u.at)}</span>
                    <a href={`#/clients/${u.clientId}`} className="bo-grow" style={{ color: 'inherit' }}>
                      {u.client}
                      <span className="bo-small bo-muted">
                        {' '}
                        · {u.offer ? pickLang(u.offer, locale) : t('vn.cardCheck')}
                        {u.by && ` · ${u.by}`}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="bo-col">
          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('vn.staff')}</h2>
              {hasOwner && (
                <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" onClick={() => invite.mutate({ id })} disabled={invite.isPending} title={t('vn.newOwnerLinkHint')}>
                  {t('vn.newOwnerLink')}
                </button>
              )}
            </div>
            {d.members.length === 0 ? (
              <p className="bo-hint">{t('vn.noStaff')}</p>
            ) : (
              <ul className="bo-list">
                {d.members.map((m) => (
                  <li key={m.id}>
                    <span className="bo-grow">
                      <b>{m.name}</b>
                      <span className="bo-small bo-muted" style={{ display: 'block' }}>
                        {t(`vn.role.${m.role}`)}
                        {m.username && ` · @${m.username}`} · {date(m.since)}
                      </span>
                    </span>
                    <button type="button" className="bo-iconbtn" onClick={() => confirm(t('vn.removeStaff', { name: m.name })) && removeMember.mutate({ id: m.id })} aria-label={t('chat.tplDelete')}>
                      <I name="close" size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {hasOwner && d.ownerInviteUrl && (
              <div style={{ marginTop: 10 }}>
                <CopyLink url={d.ownerInviteUrl} />
              </div>
            )}
            <p className="bo-hint" style={{ marginTop: 10 }}>
              {t('vn.staffHint')}
            </p>
          </section>

          <section className="bo-panel" style={{ padding: 10 }}>
            {points.length ? <MapView points={points} height={220} focus={[d.lat!, d.lng!]} /> : <p className="bo-hint" style={{ padding: 12 }}>{t('crm.noCoords')}</p>}
          </section>
          {d.note && <p className="bo-side-panel__note">{d.note}</p>}
        </div>
      </div>

      {dlg === 'edit' && <VenueDialog venue={d} onClose={() => setDlg(null)} onDone={refresh} />}
      {dlg && typeof dlg === 'object' && <OfferDialog partnerId={id} offer={dlg.offer} onClose={() => setDlg(null)} onDone={refresh} />}
    </>
  );
}

function VenueDialog({ venue, onClose, onDone }: { venue?: { id: string; name: string; category: string; offer: Record<string, string>; address: string | null; phone: string | null; lat: number | null; lng: number | null; note: string | null; active: boolean; sales: { id: string } | null }; onClose: () => void; onDone?: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const team = useQuery(trpc.bo.partners.salesTeam.queryOptions());
  const [f, setF] = useState({
    name: venue?.name ?? '',
    category: venue?.category ?? 'cafe',
    offer: { ...EMPTY, ...(venue?.offer ?? {}) } as L5,
    address: venue?.address ?? '',
    phone: venue?.phone ?? '',
    coords: venue?.lat != null && venue.lng != null ? `${venue.lat}, ${venue.lng}` : '',
    note: venue?.note ?? '',
    active: venue?.active ?? true,
    salesId: venue?.sales?.id ?? '',
  });
  const save = useMutation(
    trpc.bo.partners.saveVenue.mutationOptions({
      onSuccess: (r) => {
        qc.invalidateQueries({ queryKey: trpc.bo.partners.venues.queryKey() });
        onDone?.();
        onClose();
        if (!venue) navigate(`/venues/${r.id}`);
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  const coords = f.coords.trim() ? parseCoords(f.coords) : null;
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={venue ? t('vn.editTitle') : t('vn.add')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button
            type="button"
            className="bo-btn"
            disabled={!f.name.trim() || (!!f.coords.trim() && !coords) || save.isPending}
            onClick={() =>
              save.mutate({
                id: venue?.id,
                name: f.name,
                category: f.category as (typeof VENUE_CATEGORIES)[number],
                offer: f.offer,
                address: f.address || null,
                phone: f.phone || null,
                lat: coords?.lat ?? null,
                lng: coords?.lng ?? null,
                note: f.note || null,
                active: f.active,
                salesId: f.salesId || null,
              })
            }
          >
            {t('ui.save')}
          </button>
        </>
      }
    >
      <div className="bo-row2">
        <Field label={t('crm.name')}>
          <input className="bo-input" value={f.name} maxLength={100} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label={t('crm.category')}>
          <select className="bo-select" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
            {VENUE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`venue.cat.${c}`)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <L5Fields label={t('vn.blurb')} hint={t('vn.blurbHint')} value={f.offer} onChange={(offer) => setF({ ...f, offer })} />
      <div className="bo-row2">
        <Field label={t('crm.address')}>
          <input className="bo-input" value={f.address} maxLength={200} onChange={(e) => setF({ ...f, address: e.target.value })} />
        </Field>
        <Field label={t('chat.phone')}>
          <input className="bo-input" value={f.phone} maxLength={40} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        </Field>
      </div>
      <div className="bo-row2">
        <Field label={t('vn.coords')} hint={f.coords.trim() && !coords ? t('crm.coordsBad') : t('crm.coordsPh')}>
          <input className="bo-input" value={f.coords} onChange={(e) => setF({ ...f, coords: e.target.value })} placeholder="40.1811, 44.5136" />
        </Field>
        <Field label={t('ag.sales')}>
          <select className="bo-select" value={f.salesId} onChange={(e) => setF({ ...f, salesId: e.target.value })}>
            <option value="">—</option>
            {team.data?.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={t('crm.note')}>
        <textarea className="bo-textarea" style={{ minHeight: 60 }} value={f.note} maxLength={2000} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </Field>
      <label className="bo-check">
        <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
        {t('vn.activeLabel')}
      </label>
    </Dialog>
  );
}

/** Текст на 5 языках: русский обязателен, остальные — по желанию (клиент увидит русский, если перевода нет) */
function L5Fields({ label, hint, value, onChange }: { label: string; hint?: string; value: L5; onChange: (v: L5) => void }) {
  const [open, setOpen] = useState(!!(value.en || value.hy || value.zh || value.es));
  return (
    <div className="bo-field">
      <span className="bo-label">{label} · RU</span>
      <input className="bo-input" value={value.ru} maxLength={300} onChange={(e) => onChange({ ...value, ru: e.target.value })} />
      {hint && <span className="bo-hint">{hint}</span>}
      <button type="button" className="bo-side__link" style={{ color: 'var(--bc-accent)', padding: 0, alignSelf: 'flex-start' }} onClick={() => setOpen((o) => !o)}>
        EN · HY · ZH · ES {open ? '▴' : '▾'}
      </button>
      {open && (
        <div className="bo-row2">
          {(['en', 'hy', 'zh', 'es'] as const).map((l) => (
            <input key={l} className="bo-input" value={value[l]} maxLength={300} placeholder={l.toUpperCase()} onChange={(e) => onChange({ ...value, [l]: e.target.value })} />
          ))}
        </div>
      )}
    </div>
  );
}

function OfferDialog({ partnerId, offer, onClose, onDone }: { partnerId: string; offer?: { id: string; title: Record<string, string>; minLevel: 'BAREV_PLUS' | 'ELITE' | null; limit: 'NONE' | 'ONCE' | 'WEEK' | 'MONTH'; limitCount: number; active: boolean }; onClose: () => void; onDone: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const [f, setF] = useState({
    title: { ...EMPTY, ...(offer?.title ?? {}) } as L5,
    minLevel: offer?.minLevel ?? null,
    limit: offer?.limit ?? 'NONE',
    limitCount: String(offer?.limitCount ?? 1),
    active: offer?.active ?? true,
  });
  const save = useMutation(
    trpc.bo.partners.saveOffer.mutationOptions({
      onSuccess: () => {
        onDone();
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={offer ? t('vn.editOffer') : t('vn.addOffer')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button
            type="button"
            className="bo-btn"
            disabled={!f.title.ru.trim() || save.isPending}
            onClick={() => save.mutate({ id: offer?.id, partnerId, title: f.title, minLevel: f.minLevel, limit: f.limit, limitCount: Math.max(1, Number(f.limitCount) || 1), active: f.active })}
          >
            {t('ui.save')}
          </button>
        </>
      }
    >
      <L5Fields label={t('vn.offerTitle')} hint={t('vn.offerTitleHint')} value={f.title} onChange={(title) => setF({ ...f, title })} />
      <Field label={t('vn.forWhom')}>
        <div className="bo-pills">
          {([null, 'BAREV_PLUS', 'ELITE'] as const).map((l) => (
            <button key={l ?? 'any'} type="button" className="bo-pill" aria-pressed={f.minLevel === l} onClick={() => setF({ ...f, minLevel: l })}>
              {l ? t(`vn.level.${l}`) : t('vn.level.any')}
            </button>
          ))}
        </div>
      </Field>
      <div className="bo-row2">
        <Field label={t('vn.howOften')}>
          <select className="bo-select" value={f.limit} onChange={(e) => setF({ ...f, limit: e.target.value as typeof f.limit })}>
            {(['NONE', 'ONCE', 'WEEK', 'MONTH'] as const).map((l) => (
              <option key={l} value={l}>
                {t(`vn.limitOpt.${l}`)}
              </option>
            ))}
          </select>
        </Field>
        {(f.limit === 'WEEK' || f.limit === 'MONTH') && (
          <Field label={t('vn.times')}>
            <input className="bo-input" inputMode="numeric" value={f.limitCount} onChange={(e) => setF({ ...f, limitCount: e.target.value.replace(/\D/g, '') })} />
          </Field>
        )}
      </div>
      <label className="bo-check">
        <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
        {t('vn.offerActive')}
      </label>
    </Dialog>
  );
}
